/**
 * What the Copilot docked on the quote builder (src/components/CopilotDock.jsx, LP-402) keeps and sends,
 * and what the page (src/pages/QuoteEditorPage.jsx) does with unsaved edits before a message (LP-443).
 * Imported by the browser bundle: plain functions, no Node imports.
 *
 * One thread per quote version. Every message is made with dockTurn(quote), which stamps it with
 * dockKey(quote), and only the current version's messages are shown and sent as history, so nothing
 * said about one quote or version is sent with another's active_quote_id. (The page also remounts the
 * dock per version: key={dockKey(q)}.) A message that was not sent, and the dock's own notice saying
 * so, stay on screen but are local: never history (notSent).
 *
 * Before a message the page saves what was typed, so the Copilot changes what is on screen. A draft
 * does so as before. Any other status (an approved quote) never saves silently: that save returns it
 * to draft and it must be approved again, so the partner confirms that first or clicks Save. For the same
 * reason a Copilot change can be undone only on a draft (undoGate, LP-442).
 */

/** The thread a quote version's messages belong to (its id and version), or null without a quote. */
export function dockKey(quote) {
  return quote && quote.id ? `${quote.id}:v${quote.version ?? ''}` : null;
}

/** This version's messages: the ones the dock shows. */
export function threadFor(messages, key) {
  return key ? (messages || []).filter((m) => m && m.quote === key) : [];
}

/** One dock message on this quote version (more: drafts, change, local…). */
export function dockTurn(quote, role, content, more) {
  return { role, content, ...more, quote: dockKey(quote) };
}

/** The messages after `turn` was not sent: it and the notice `why` stay on screen, both local. */
export function notSent(messages, turn, why, quote) {
  return [...messages.map((m) => (m === turn ? { ...m, local: true } : m)), dockTurn(quote, 'assistant', why, { local: true })];
}

/** The dock's request body for one message (POST /api/copilot/chat): history from this version's thread only, never a local message. */
export function dockRequest(message, messages, quote) {
  const history = threadFor(messages, dockKey(quote)).filter((m) => !m.local).slice(-8).map((m) => ({ role: m.role, content: m.content }));
  return { message, history, active_quote_id: quote.id };
}

/** Said under a Copilot change's Undo on an approved quote (the server refuses that Undo with the same words). */
export const UNDO_NOT_DRAFT = 'Undo is for drafts: this quote is approved, and an Undo would return it to draft. Edit it and click Save if it needs to change; it must then be approved again.';

/**
 * Why no Copilot change can be undone on this quote now, or null (LP-442, LP-443's finding). An Undo saves
 * the form, and on an approved quote that save withdraws the approval with no prompt, so Undo is for drafts
 * only: an approved quote changes only by an edit the partner saves on purpose. A version that can no
 * longer be edited (sent, older) has no Undo either.
 */
export function undoGate({ status, canEdit }) {
  if (!canEdit) return 'This version can no longer be edited.';
  if (status !== 'draft') return UNDO_NOT_DRAFT;
  return null;
}

/** Said in the dock when a message is held on an approved quote (nothing was saved or sent). */
export const HELD_APPROVED = 'Not sent: this quote is approved, and saving your edits returns it to draft. Click Save first, or ask again and confirm.';

/**
 * Before a Copilot message: { status, canEdit, dirty, label, confirm(question) → boolean, save() }.
 * Nothing unsaved, or a quote that cannot be edited: nothing is saved. A draft: save. Any other status:
 * save only once confirm() says yes to a question saying it goes back to draft. Resolves to null when
 * the message may go, or to the reason it was held (nothing saved, the status unchanged).
 */
export async function beforeCopilotSave({ status, canEdit, dirty, label, confirm, save }) {
  if (!canEdit || !dirty) return null;
  if (status !== 'draft') {
    const ok = await confirm(`${label} is approved. Saving your edits so Copilot can work on them returns it to draft, and it must be approved again before it is sent.\n\nSave and send your message to Copilot?`);
    if (ok !== true) return HELD_APPROVED;
  }
  await save();
  return null;
}
